/**
 * TenantProvisioningWizard — iter83 (P1 Task 1)
 *
 * Guided 3-step modal for the Platform Console "Create tenant" CTA.
 * Replaces the single-step CreateTenantModal so the operator can
 * provision the tenant AND invite its first Site Admin in one flow.
 *
 *   Step 1 — Tenant info     → POST /platform-admin/tenants
 *   Step 2 — First admin     → POST /platform-admin/tenants/{id}/invite-admin
 *   Step 3 — Confirmation    → magic link + email status (skippable Step 2)
 *
 * Each step commits independently to the backend. If the operator
 * cancels mid-flow, the tenant they already created stays (visible in
 * the table). Step 2 is optional — they can finish without an invite
 * and add one later via the row's actions.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    X, ChevronRight, CheckCircle2, UserPlus, Copy, Mail, Loader2,
    Globe, Smartphone, Building2, ArrowLeft, SkipForward,
} from "lucide-react";

const STEP_TENANT = 1;
const STEP_ADMIN = 2;
const STEP_DONE = 3;

export default function TenantProvisioningWizard({ onClose, onCreated }) {
    const [step, setStep] = useState(STEP_TENANT);

    // Step 1 state — tenant info
    const [tenantForm, setTenantForm] = useState({
        slug: "",
        name: "",
        custom_domain: "",
        mobile_enabled: false,
    });
    const [tenant, setTenant] = useState(null); // created tenant doc

    // Step 2 state — first admin invite
    const [adminForm, setAdminForm] = useState({ email: "", name: "" });
    const [invite, setInvite] = useState(null); // {magic_link_preview_only, email_sent, expires_at, ...}

    // shared
    const [busy, setBusy] = useState(false);

    const submitTenant = async () => {
        const slug = tenantForm.slug.trim().toLowerCase();
        const name = tenantForm.name.trim();
        if (!slug || !name) {
            toast.error("Slug and name are required");
            return;
        }
        setBusy(true);
        try {
            const body = { slug, name, mobile_enabled: tenantForm.mobile_enabled };
            if (tenantForm.custom_domain.trim()) {
                body.custom_domain = tenantForm.custom_domain.trim().toLowerCase();
            }
            const r = await api.createTenant(body);
            setTenant(r.tenant);
            // Notify parent immediately so the table reflects the new
            // tenant even if the operator abandons the wizard at step 2.
            onCreated?.(r.tenant);
            toast.success(`Tenant "${r.tenant.name}" created`);
            setStep(STEP_ADMIN);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create tenant");
        }
        setBusy(false);
    };

    const submitInvite = async () => {
        const email = adminForm.email.trim().toLowerCase();
        if (!email || !email.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        setBusy(true);
        try {
            const r = await api.inviteTenantAdmin(tenant.id, email, adminForm.name.trim() || undefined);
            setInvite(r);
            toast.success(r.email_sent ? "Invite email sent" : "Invite created (email delivery failed — copy link)");
            setStep(STEP_DONE);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send invite");
        }
        setBusy(false);
    };

    const skipInvite = () => setStep(STEP_DONE);

    const copyLink = async () => {
        if (!invite?.magic_link_preview_only) return;
        try {
            await navigator.clipboard.writeText(invite.magic_link_preview_only);
            toast.success("Magic link copied");
        } catch {
            toast.error("Copy failed — select the link manually");
        }
    };

    return (
        <div
            data-testid="tenant-provisioning-wizard"
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="brutal-card max-w-lg w-full p-5 space-y-5"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-center justify-between">
                    <h3 className="font-display font-extrabold text-lg flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-cyber" />
                        Provision Tenant
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-zinc-500 hover:text-white"
                        data-testid="wizard-close-btn"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </header>

                <StepIndicator step={step} />

                {step === STEP_TENANT && (
                    <StepTenant
                        form={tenantForm}
                        setForm={setTenantForm}
                        busy={busy}
                        onSubmit={submitTenant}
                        onCancel={onClose}
                    />
                )}

                {step === STEP_ADMIN && tenant && (
                    <StepAdmin
                        tenant={tenant}
                        form={adminForm}
                        setForm={setAdminForm}
                        busy={busy}
                        onSubmit={submitInvite}
                        onSkip={skipInvite}
                        onBack={() => setStep(STEP_TENANT)}
                    />
                )}

                {step === STEP_DONE && (
                    <StepDone
                        tenant={tenant}
                        invite={invite}
                        onCopy={copyLink}
                        onClose={onClose}
                    />
                )}
            </div>
        </div>
    );
}

// ---- step indicator -------------------------------------------------

function StepIndicator({ step }) {
    const items = [
        { idx: STEP_TENANT, label: "Tenant", icon: Globe },
        { idx: STEP_ADMIN, label: "First Admin", icon: UserPlus },
        { idx: STEP_DONE, label: "Done", icon: CheckCircle2 },
    ];
    return (
        <ol
            className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold"
            data-testid="wizard-step-indicator"
        >
            {items.map((it, i) => {
                const Icon = it.icon;
                const active = step === it.idx;
                const done = step > it.idx;
                return (
                    <li
                        key={it.idx}
                        className="flex items-center gap-1.5"
                        data-testid={`wizard-step-${it.idx}-${active ? "active" : done ? "done" : "pending"}`}
                    >
                        <span
                            className={`w-6 h-6 border-2 flex items-center justify-center ${
                                active
                                    ? "bg-cyber border-cyber text-black"
                                    : done
                                        ? "bg-emerald-500 border-emerald-500 text-black"
                                        : "bg-transparent border-[#333] text-zinc-600"
                            }`}
                        >
                            <Icon className="w-3 h-3" strokeWidth={2.5} />
                        </span>
                        <span className={active ? "text-cyber" : done ? "text-emerald-400" : "text-zinc-600"}>
                            {it.label}
                        </span>
                        {i < items.length - 1 && <ChevronRight className="w-3 h-3 text-zinc-700" />}
                    </li>
                );
            })}
        </ol>
    );
}

// ---- step 1: tenant -------------------------------------------------

function StepTenant({ form, setForm, busy, onSubmit, onCancel }) {
    return (
        <div className="space-y-4" data-testid="wizard-step-tenant">
            <p className="text-xs text-zinc-500 leading-relaxed">
                Create the tenant first. You can fill in the first Site Admin on the next step,
                or skip and do that later from the tenants list.
            </p>

            <label className="block text-xs space-y-1">
                <span className="block text-zinc-500 uppercase tracking-wider text-[10px] font-bold">
                    Slug (used for subdomain routing)
                </span>
                <input
                    data-testid="wizard-tenant-slug"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="acme-sports"
                    className="brutal-input w-full font-mono"
                    autoFocus
                />
            </label>

            <label className="block text-xs space-y-1">
                <span className="block text-zinc-500 uppercase tracking-wider text-[10px] font-bold">
                    Display name
                </span>
                <input
                    data-testid="wizard-tenant-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="ACME Sports"
                    className="brutal-input w-full"
                />
            </label>

            <label className="block text-xs space-y-1">
                <span className="block text-zinc-500 uppercase tracking-wider text-[10px] font-bold">
                    Custom domain (optional)
                </span>
                <input
                    data-testid="wizard-tenant-domain"
                    value={form.custom_domain}
                    onChange={(e) => setForm({ ...form, custom_domain: e.target.value })}
                    placeholder="crew.example.com"
                    className="brutal-input w-full font-mono"
                />
            </label>

            <label className="flex items-center gap-2 cursor-pointer text-xs">
                <input
                    data-testid="wizard-tenant-mobile"
                    type="checkbox"
                    checked={form.mobile_enabled}
                    onChange={(e) => setForm({ ...form, mobile_enabled: e.target.checked })}
                    className="w-4 h-4 accent-cyber cursor-pointer"
                />
                <Smartphone className="w-3.5 h-3.5 text-zinc-500" />
                <span className="text-zinc-300">Enable mobile access on creation</span>
            </label>

            <div className="flex justify-end gap-2 pt-3 border-t border-[#222]">
                <button
                    type="button"
                    onClick={onCancel}
                    className="brutal-btn-ghost text-xs py-2"
                    data-testid="wizard-tenant-cancel"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={onSubmit}
                    disabled={busy}
                    data-testid="wizard-tenant-next"
                    className="brutal-btn-primary text-xs py-2 disabled:opacity-60"
                >
                    {busy ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating…</>
                    ) : (
                        <>Create & continue <ChevronRight className="w-3.5 h-3.5" /></>
                    )}
                </button>
            </div>
        </div>
    );
}

// ---- step 2: invite first admin -------------------------------------

function StepAdmin({ tenant, form, setForm, busy, onSubmit, onSkip, onBack }) {
    return (
        <div className="space-y-4" data-testid="wizard-step-admin">
            <div className="brutal-card-light p-3 text-xs">
                <p className="text-zinc-400">
                    Tenant <strong className="text-white">{tenant.name}</strong> is ready.
                    Now invite its first Site Admin — they'll get a magic-link email to set up their password.
                </p>
            </div>

            <label className="block text-xs space-y-1">
                <span className="block text-zinc-500 uppercase tracking-wider text-[10px] font-bold">
                    Admin email
                </span>
                <input
                    data-testid="wizard-admin-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="admin@acme.com"
                    className="brutal-input w-full"
                    autoFocus
                />
            </label>

            <label className="block text-xs space-y-1">
                <span className="block text-zinc-500 uppercase tracking-wider text-[10px] font-bold">
                    Display name (optional)
                </span>
                <input
                    data-testid="wizard-admin-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Jane Coach"
                    className="brutal-input w-full"
                />
            </label>

            <div className="flex justify-between items-center gap-2 pt-3 border-t border-[#222]">
                <button
                    type="button"
                    onClick={onBack}
                    disabled={busy}
                    className="brutal-btn-ghost text-xs py-2 disabled:opacity-60"
                    data-testid="wizard-admin-back"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onSkip}
                        disabled={busy}
                        className="brutal-btn-ghost text-xs py-2 disabled:opacity-60"
                        data-testid="wizard-admin-skip"
                    >
                        <SkipForward className="w-3.5 h-3.5" /> Skip for now
                    </button>
                    <button
                        type="button"
                        onClick={onSubmit}
                        disabled={busy || !form.email.trim()}
                        data-testid="wizard-admin-send"
                        className="brutal-btn-primary text-xs py-2 disabled:opacity-60"
                    >
                        {busy ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                        ) : (
                            <><Mail className="w-3.5 h-3.5" /> Send invite</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---- step 3: done ---------------------------------------------------

function StepDone({ tenant, invite, onCopy, onClose }) {
    const skipped = !invite;
    return (
        <div className="space-y-4" data-testid="wizard-step-done">
            <div className="flex items-center gap-3">
                <span className="w-10 h-10 bg-emerald-500 border-2 border-black flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-black" strokeWidth={2.5} />
                </span>
                <div>
                    <h4 className="font-display font-extrabold text-base text-white">
                        {skipped ? "Tenant provisioned" : "Provisioning complete"}
                    </h4>
                    <p className="text-xs text-zinc-500">
                        {tenant?.name} is live{!skipped ? " — invite is on its way" : ""}.
                    </p>
                </div>
            </div>

            {invite && (
                <div className="brutal-card-light p-3 space-y-2 text-xs">
                    <div className="flex justify-between text-zinc-400">
                        <span>Invitee</span>
                        <span className="font-mono text-white" data-testid="wizard-done-email">{invite.email}</span>
                    </div>
                    <div className="flex justify-between text-zinc-400">
                        <span>Email delivery</span>
                        <span
                            data-testid="wizard-done-email-status"
                            className={invite.email_sent ? "text-emerald-400 font-bold" : "text-amber-400 font-bold"}
                        >
                            {invite.email_sent ? "Sent" : "Failed — copy link"}
                        </span>
                    </div>
                    <div className="pt-2 border-t border-[#222]">
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">
                            Magic link (one-shot, expires 7 days)
                        </p>
                        <div className="flex gap-2 items-start">
                            <code
                                data-testid="wizard-done-magic-link"
                                className="block flex-1 break-all text-[10px] bg-black p-2 border border-[#333] font-mono text-zinc-300"
                            >
                                {invite.magic_link_preview_only}
                            </code>
                            <button
                                type="button"
                                onClick={onCopy}
                                className="brutal-btn-ghost text-xs py-1.5 shrink-0"
                                data-testid="wizard-done-copy-link"
                            >
                                <Copy className="w-3 h-3" /> Copy
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {skipped && (
                <div className="brutal-card-light p-3 text-xs text-zinc-400 leading-relaxed">
                    No admin invited yet. You can do this later from the tenants list — open
                    the row and use <strong className="text-white">Invite admin</strong>.
                </div>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-[#222]">
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="wizard-done-close"
                    className="brutal-btn-primary text-xs py-2"
                >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Finish
                </button>
            </div>
        </div>
    );
}

// Re-export name for direct import compatibility (parallel to the
// old CreateTenantModal symbol).
export { TenantProvisioningWizard };
